import { Component, EventEmitter, inject, Output } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DocumentState } from '@app/core/enums/document/document-state.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { CreateDocumentInput } from '@app/core/model/entities/document/document';
import { CreateDocumentData } from '@app/shared/components/file-uploader/file-uploader.component';
import { TranslateService } from '@ngx-translate/core';
import { AccessManager } from '@services/managers/access.manager';

@Component({
    templateUrl: './upload-file-modal.component.html',
    styleUrls: ['./upload-file-modal.component.scss'],
    standalone: false
})
export class UploadFileModalComponent {
  protected data = inject(MAT_DIALOG_DATA);
  private documentsToCreate: CreateDocumentData[] = [];
  private isUploadInProgress: boolean = false;
  public preventUploadFiles: boolean;
  protected missingRequiredFields: { code: string, label: string }[];

  public removeAllFilesEmitter = new EventEmitter<void>();
  @Output() public fileUploaded = new EventEmitter<File>();

  private accessManager = inject(AccessManager);
  private translate = inject(TranslateService);

  constructor() {
    this.missingRequiredFields = [
      {code: 'name', label: this.translate.instant('LABEL.DOCUMENT_NAME')},
      {code: 'entityId', label: this.translate.instant('LABEL.ENTITY_ID')},
      {code: 'entityType', label: this.translate.instant('LABEL.DOCUMENT_ENTITY_TYPE')},
      {code: 'type', label: this.translate.instant('LABEL.DOCUMENT_TYPE')},
      {code: 'state', label: this.translate.instant('LABEL.DOCUMENTSTATE')}
    ].filter(({code}) => !this.accessManager.hasFieldWriteAccess(code, EntityTypeEnum.DOCUMENT));
  }

  /**
   * Remove all files from the file uploader.
   */
  public removeAllFiles(): void {
    this.documentsToCreate = [];
    this.removeAllFilesEmitter.next();
  }

  /**
   * Check if the upload is invalid.
   * @returns true if the upload is invalid, false otherwise.
   */
  public isUploadInvalid(): boolean {
    return this.isUploadInProgress;
  }

  /**
   * Call when the upload of a file is in progress.
   * @param event true if the upload is in progress, false otherwise.
   */
  public uploadIsInProgress(event: boolean): void {
    this.isUploadInProgress = event;
  }

  /**
   * Call when a new document is created.
   * @param event the new document.
   */
  public createNewDocuments(event: CreateDocumentData): void {
    this.documentsToCreate.push(event);
  }

  /**
   * Create a Document input for each row of the properties grid.
   * @param entityId ID of the entity the new documents will be related to.
   * @param entityType Type of the entity the new documents will be related to.
   * @returns Document inputs.
   */
  public getCreateDocumentsInput(entityId: string, entityType: EntityTypeEnum): CreateDocumentInput[] {
    return this.documentsToCreate.map(documentToCreate => {
      return {
        name: documentToCreate.documentName,
        fileName: documentToCreate.fileName,
        type: this.data.uploadData.documentType,
        state: DocumentState.ACTIVE,
        entityId,
        entityType
      };
    });
  }
}
